import { Company } from 'module/api/company/types';
import { PageLoading } from 'module/ui/components/atoms/loading';
import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SignalsV2ReasonsView } from './reasons-view';
import { SignalsV2Layout, SignalsV2ViewType } from './signals-v2-layout';
import { SignalsV2SummaryView } from './summary-view';
import { SignalsV2TimelineView } from './timeline-view';

interface SignalsV2Props {
  company: Company;
  projectId?: number;
}

const getInitialViewFromSearchParams = (
  searchParams: URLSearchParams,
): SignalsV2ViewType => {
  const view = searchParams.get('view');

  if (view === 'timeline' || view === 'summary' || view === 'reasons') {
    return view as SignalsV2ViewType;
  }

  return 'summary';
};

export const SignalsV2: React.FC<SignalsV2Props> = ({ company, projectId }) => {
  const [searchParams] = useSearchParams();
  const [selectedView, setSelectedView] = useState<SignalsV2ViewType>(() =>
    getInitialViewFromSearchParams(searchParams),
  );
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const hasMountedRef = useRef<boolean>(false);

  useEffect(() => {
    hasMountedRef.current = true;
    setIsInitialLoading(false);
  }, []);

  // Sync selectedView when navigating with ?view= in URL (e.g. from signal card)
  useEffect(() => {
    const viewFromUrl = getInitialViewFromSearchParams(searchParams);

    setSelectedView(viewFromUrl);
  }, [searchParams]);

  // Only show full page loading on very first mount
  if (isInitialLoading && !hasMountedRef.current) {
    return <PageLoading />;
  }

  return (
    <SignalsV2Layout selectedView={selectedView} onViewChange={setSelectedView}>
      {selectedView === 'summary' ? (
        <SignalsV2SummaryView company={company} />
      ) : selectedView === 'reasons' ? (
        <SignalsV2ReasonsView company={company} />
      ) : selectedView === 'timeline' ? (
        <SignalsV2TimelineView company={company} projectId={projectId} />
      ) : null}
    </SignalsV2Layout>
  );
};
